import React from 'react';
import styled from '@emotion/styled';
import Typography from '@mui/material/Typography';
import { useTranslate } from 'hooks/useTranslate';
import LazyLoadImage from 'components/LazyLoadImage';

const Wrapper = styled('aside')(({ theme }) => ({
  margin: '32px 0',
  padding: 24,
  border: `1px solid ${theme.palette.grey[200]}`,
  borderRadius: 12,
  backgroundColor: theme.palette.grey[50]
}));

const HeaderRow = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: 20,
  [theme.breakpoints.down('sm')]: {
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: 12
  }
}));

const PhotoWrapper = styled('div')(() => ({
  width: 96,
  height: 96,
  borderRadius: '50%',
  overflow: 'hidden',
  flexShrink: 0,
  '& img': {
    width: '100%',
    height: '100%',
    objectFit: 'cover'
  }
}));

const Info = styled('div')(() => ({
  flex: 1,
  minWidth: 0
}));

const Name = styled(Typography)(({ theme }) => ({
  fontSize: 20,
  fontWeight: 600,
  color: theme.palette.textPrimary.main,
  marginBottom: 4
}));

const JobTitle = styled(Typography)(({ theme }) => ({
  fontSize: 14,
  color: theme.palette.textSecondary?.main || theme.palette.grey[600],
  marginBottom: 4
}));

const Expertise = styled(Typography)(({ theme }) => ({
  fontSize: 13,
  color: theme.palette.grey[400],
  fontStyle: 'italic'
}));

const Bio = styled(Typography)(({ theme }) => ({
  marginTop: 16,
  fontSize: 15,
  lineHeight: 1.6,
  color: theme.palette.textSecondary?.main || theme.palette.grey[600]
}));

const Socials = styled('ul')(() => ({
  listStyle: 'none',
  padding: 0,
  margin: '16px 0 0 0',
  display: 'flex',
  flexWrap: 'wrap',
  gap: 12
}));

const SocialLink = styled('a')(({ theme }) => ({
  fontSize: 13,
  color: theme.palette.primary.dark,
  textDecoration: 'none',
  padding: '4px 10px',
  border: `1px solid ${theme.palette.grey[200]}`,
  borderRadius: 6,
  backgroundColor: theme.palette.white?.main || '#fff'
}));

const formatPlatform = (platform) => {
  if (!platform) return 'Link';
  return platform.charAt(0).toUpperCase() + platform.slice(1);
};

const AuthorProfile = ({ author }) => {
  const t = useTranslate('Posts');
  if (!author) return null;

  const name = author.nameUA || author.nameEN || '';
  const jobTitle = author.jobTitleUA || author.jobTitleEN || '';
  const expertise = author.expertiseUA || author.expertiseEN || '';
  const bio = author.bioUA || author.bioEN || '';
  const photoSrc = author.picture ? `${author.picture}/w192h192` : null;

  return (
    <Wrapper itemScope itemType="https://schema.org/Person" itemProp="author">
      <HeaderRow>
        {photoSrc && (
          <PhotoWrapper>
            <LazyLoadImage src={photoSrc} alt={name} width="96" height="96" itemProp="image" />
          </PhotoWrapper>
        )}
        <Info>
          <Name as="h2" itemProp="name">
            {name}
          </Name>
          {jobTitle && <JobTitle itemProp="jobTitle">{jobTitle}</JobTitle>}
          {expertise && <Expertise itemProp="knowsAbout">{expertise}</Expertise>}
        </Info>
      </HeaderRow>
      {bio && <Bio itemProp="description">{bio}</Bio>}
      {Array.isArray(author.socialLinks) && author.socialLinks.length > 0 && (
        <Socials aria-label={t('AuthorSocials') || 'Social links'}>
          {author.socialLinks
            .filter((link) => link && link.url)
            .map((link, i) => (
              <li key={`${link.platform || 'link'}-${i}`}>
                <SocialLink
                  href={link.url}
                  target="_blank"
                  rel="noopener noreferrer me"
                  itemProp="sameAs"
                >
                  {formatPlatform(link.platform)}
                </SocialLink>
              </li>
            ))}
        </Socials>
      )}
    </Wrapper>
  );
};

export default AuthorProfile;
